// Unpublished Work © 2026 Deere & Company.
import {getJobTemplates, updateJobTemplate} from 'Services/job-service';
import {alphaNumericCompare} from 'Ui/models/maintenance';
import {fetchAndFilterInactiveOperators} from 'OnLabor/workboard/utils/fetch-operators';
import {getCutHistory, getFleetEquipment} from 'Services/membership-service';
import {isAbortError} from 'Src/ui-core/utils/react-utils';
import {cloneDeep} from 'lodash';
export const UPDATE_DETAILS_FIELD = 'UPDATE_DETAILS_FIELD';
export const SET_LOADING_DETAILS = 'SET_LOADING_DETAILS';
export const SET_JOBS = 'SET_JOBS';
export const SET_LOADING_JOBS = 'SET_LOADING_JOBS';
export const SET_SELECTED_JOBS = 'SET_SELECTED_JOBS';
export const REMOVE_SELECTED_JOBS = 'REMOVE_SELECTED_JOBS';
export const SET_LOADING_OPERATORS = 'SET_LOADING_OPERATORS';
export const SET_TEAM_MEMBERS = 'SET_TEAM_MEMBERS';
export const SELECT_OPERATOR = 'SELECT_OPERATOR';
export const DELETE_SELECTED_OPERATOR = 'DELETE_SELECTED_OPERATOR';
export const SET_CUT_HISTORY = 'SET_CUT_HISTORY';
export const SET_SELECTED_MOWING_PATTERNS = 'SET_SELECTED_MOWING_PATTERNS';
export const RESET_SETUP_STATE = 'RESET_SETUP_STATE';
export const SET_FLEET_EQUIPMENT = 'SET_FLEET_EQUIPMENT';
export const SET_LOADING_FLEET_EQUIPMENT = 'SET_LOADING_FLEET_EQUIPMENT';
export const SET_WORK_ITEMS = 'SET_WORK_ITEMS';
export const SET_JOBS_TO_REMOVE = 'SET_JOBS_TO_REMOVE';
export const SET_LOADING_SETUP = 'SET_LOADING_SETUP';
export const SET_IS_COPY = 'SET_IS_COPY';
export const SET_COPY_WORKBOARD_MODE = 'SET_COPY_WORKBOARD_MODE';
export const SET_OPERATORS_TO_DELETE = 'SET_OPERATORS_TO_DELETE';
export const SET_ROW_ORDER = 'SET_ROW_ORDER';
export const SET_INITIAL_STATE = 'SET_INITIAL_STATE';
export const SET_IS_SAVING = 'SET_IS_SAVING';
export const SET_NAME_VALIDATION = 'SET_NAME_VALIDATION';

// TODO Remove next line after moving logic to redux
/* eslint-disable max-statements */
function sanitizeWorkItemsMap(workItemsMap, operators) {
    const result = {};

    for (const [key, item] of Object.entries(workItemsMap)) {
        const operatorId = item.operatorId ?? item.appUserId ?? null;
        const teamMember = item.teamMember ?? operators.find((op) => op.id === item.operatorId) ?? null;
        const note = item.notes ?? item.note ?? '';
        const actualHours = item.actualHours ?? item.actualDuration ?? null;
        const workItemId = item.workItemId ?? null;
        const workboardTemplateWorkItemId = item.workboardTemplateWorkItemId ?? workItemId ?? null;
        const workboardTemplateWorkboardId = item.workboardTemplateWorkboardId ?? null;
        const jobId = item.jobTemplateId ?? item.jobId ?? null;
        const route = item.route === undefined ? null : item.route;
        const routeCustomDefn = item.routeCustomDefn ?? [];
        const seq = item.seq ?? 1;
        const savedJobId = !item.workboardTemplateWorkItemId ? item.jobId : null;

        const equipment = Array.isArray(item.equipment) ? item.equipment : [];

        result[key] = {
            workboardTemplateWorkItemId,
            workboardTemplateWorkboardId,
            jobId,
            savedJobId,
            operatorId,
            note,
            equipment,
            route: {
                selectedRoute: route,
                routeCustomDefn
            },
            actualDuration: actualHours,
            workItemId,
            teamMember,
            seq
        };
    }

    return result;
}

export function setInitalWorkboardData({
    initialData, operatorIdAssesor, workItemIdAssesor
}) {
    let {
        selectedOperators = [],
        selectedJobs = []
    } = initialData;

    const {
        workItems = [],
        mowingPatterns = [],
        teamMembers = [],
        jobs = []
    } = initialData;

    const workItemIds = new Map();
    const operatorIds = new Set();
    const jobIds = new Set();

    if (teamMembers.length > 0 && selectedOperators.length > 0) {
        const teamById = new Map(teamMembers.map((tm) => [tm.id, tm]));

        selectedOperators = selectedOperators.map((so) => {
            const tm = teamById.get(so.operatorId);

            operatorIds.add(so.operatorId);

            return tm ? {
                ...so,
                ...tm
            } : so;
        });
    }

    if (jobs.length > 0) {
        const jobsById = new Map(jobs.map((job) => [job.jobTemplateId, job]));

        selectedJobs = selectedJobs.map((sj) => {
            const job = jobsById.get(sj.jobId);

            jobIds.add(sj.jobId);

            return job ? {
                ...sj,
                ...job
            } : sj;
        });
    }

    if (workItems.length > 0 && operatorIdAssesor && workItemIdAssesor) {
        const workItemObj = {};

        for (const item of workItems) {
            const key = `${item.jobTemplateId ?? item.jobId}::${item[operatorIdAssesor]}`;

            workItemObj[key] = {
                ...item,
                operatorId: item[operatorIdAssesor],
                workItemId: item[workItemIdAssesor]
            };
            workItemIds.set(key, item[workItemIdAssesor]);
        }
        initialData.workItems = sanitizeWorkItemsMap(workItemObj, selectedOperators);
    } else {
        initialData.workItems = {};
    }

    initialData.mowingPatterns = mowingPatterns;
    initialData.selectedOperators = selectedOperators;
    initialData.selectedJobs = selectedJobs;
    initialData.initialSelectedOperators = operatorIds;
    initialData.initialSelectedJobs = jobIds;
    initialData.initialWorkItems = workItemIds;

    const id = initialData.workboardId ?? initialData.workboardTemplateWorkboardId;

    if (id) {
        initialData.workboardId = id;
    }

    initialData.initialWorkboard = {
        details: {
            name: initialData.name || '',
            date: initialData.date,
            pinLocation: initialData.pinLocation || '',
            notes: initialData.notes || initialData.note || ''
        },
        operators: selectedOperators,
        jobs: selectedJobs,
        workItems: initialData.workItems,
        cutsAndDirections: cloneDeep(mowingPatterns),
        rowOrder: initialData.rowOrder || []
    };

    return {
        type: SET_INITIAL_STATE,
        values: initialData
    };
}

export function updateDetailsField(props) {
    const {
        field,
        value
    } = props;

    return {
        type: UPDATE_DETAILS_FIELD,
        field,
        value
    };
}

export function setLoadingDetails(loading) {
    return {
        type: SET_LOADING_DETAILS,
        loading
    };
}

export function setTeamMembers(props) {
    const {teamMembers} = props;

    return {
        type: SET_TEAM_MEMBERS,
        teamMembers
    };
}

export function setLoadingOperators() {
    return {
        type: SET_LOADING_OPERATORS
    };
}

export function selectOperator(props) {
    const {newOperators} = props;

    return {
        type: SELECT_OPERATOR,
        newSelected: [...newOperators]
    };
}

export function setWorkItems(workItems) {
    return {
        type: SET_WORK_ITEMS,
        workItems
    };
}

function refreshSelectedOperators(dispatch, getState, fetchedOperators) {
    const state = getState();
    const selectedOperators = state.setupWorkboard?.operators?.selected ?? [];
    const workItems = state.setupWorkboard?.workItems ?? {};

    const fetchedById = new Map(fetchedOperators.map((op) => [op.id, op]));

    if (selectedOperators.length > 0) {
        const newOperators = selectedOperators.map((operator) => ({
            ...operator,
            ...fetchedById.get(operator.id)
        }));

        dispatch(selectOperator({
            newOperators
        }));
    }

    const workItemEntries = Object.entries(workItems);

    if (workItemEntries.length > 0) {
        const newWorkItems = Object.fromEntries(workItemEntries.map(([key, workItem]) => {
            const fetchedTeamMember = fetchedById.get(workItem.teamMember?.id);

            if (!workItem.teamMember || !fetchedTeamMember) {
                return [key, workItem];
            }

            return [key, {
                ...workItem,
                teamMember: {
                    ...workItem.teamMember,
                    ...fetchedTeamMember
                }
            }];
        }));

        dispatch(setWorkItems(newWorkItems));
    }
}

export function fetchTeamMembers(props) {
    const {
        orgId, date, signal, tagOnTimeOff, refreshSelected
    } = props;

    return async (dispatch, getState) => {
        const operators = await fetchAndFilterInactiveOperators(orgId, date, signal, tagOnTimeOff);

        if (refreshSelected) {
            refreshSelectedOperators(dispatch, getState, operators);
        }

        dispatch(setTeamMembers({
            teamMembers: operators
        }));
    };
}

export function setOperatorsToDelete(args) {
    const {operatorsToDelete} = args;

    return {
        type: SET_OPERATORS_TO_DELETE,
        operators: [...operatorsToDelete]
    };
}

export function deleteSelectedOperators(props) {
    const {
        operators,
        idsToRemove
    } = props;

    const newOperators = operators.filter((operator) => !idsToRemove.includes(operator.id));

    return {
        type: DELETE_SELECTED_OPERATOR,
        newSelected: [...newOperators],
        idsToRemove: [...idsToRemove]
    };
}

export function setLoadingJobs(loading) {
    return {
        type: SET_LOADING_JOBS,
        loading
    };
}

export function setJobTemplates(args) {
    const {jobs} = args;

    return {
        type: SET_JOBS,
        jobs
    };
}

export function setSelectedJobs(args) {
    const {jobs} = args;

    return {
        type: SET_SELECTED_JOBS,
        newSelected: [...jobs]
    };
}

export function setJobsToRemove(args) {
    const {jobsToRemove} = args;

    return {
        type: SET_JOBS_TO_REMOVE,
        jobs: [...jobsToRemove]
    };
}

export function removeSelectedJobs(args) {
    const {
        jobs,
        idsToRemove
    } = args;

    const newJobs = jobs.filter((job) => !idsToRemove.includes(job.id));

    return {
        type: REMOVE_SELECTED_JOBS,
        newSelected: [...newJobs],
        idsToRemove: [...idsToRemove]
    };
}

function addNewJobToSelectedJobs(dispatch, getState, addedJob, formattedJobs) {
    const {setupWorkboard: {jobs: {selectedJobs: currentSelected}}} = getState();
    const newJob = formattedJobs.find((job) => job.id === addedJob);

    if (newJob && !currentSelected.some((j) => j.id === newJob.id)) {
        dispatch(setSelectedJobs({
            jobs: [...currentSelected, newJob]
        }));
    }
}

export function fetchJobTemplates(args) {
    const {
        addedJob,
        dispatcher,
        extraContent,
        operatorCountLabel
    } = args;

    return async (dispatch, getState) => {
        dispatch(setLoadingJobs(true));
        try {
            const {setupWorkboard: {jobs: {selectedJobs}}} = getState();
            const {templates} = await getJobTemplates();
            const formattedJobs = templates.filter((job) => !job.inactive).map((jobTemplate) => {
                return {
                    ...jobTemplate,
                    title: jobTemplate.title ?? '',
                    subtitle: `${operatorCountLabel}: ${jobTemplate.operatorCount ?? '---'}`,
                    id: jobTemplate.jobTemplateId,
                    extraContent: extraContent === null ? null : extraContent(operatorCountLabel, jobTemplate, dispatcher)
                };
            })
                .sort((a, b) => {
                    const fav = (b.isFavorite ? 1 : 0) - (a.isFavorite ? 1 : 0);

                    if (fav !== 0) {
                        return fav;
                    }

                    return alphaNumericCompare(a.title, b.title);
                });

            // Update the selected jobs with any new version
            if (selectedJobs.length > 0) {
                const updatedSelectedJobs = selectedJobs.map((job) => {
                    const newInfo = formattedJobs.find((fj) => fj.id === job.id);

                    return newInfo ? {
                        ...newInfo
                    } : null;
                }).filter(Boolean);

                dispatch(setSelectedJobs({
                    jobs: updatedSelectedJobs
                }));
            }

            // Add the new job to the selected ones
            if (addedJob) {
                addNewJobToSelectedJobs(dispatch, getState, addedJob, formattedJobs);
            }

            dispatch(setJobTemplates({
                jobs: formattedJobs
            }));
        } catch (error) {
            if (!isAbortError(error)) {
                return;
            }
        } finally {
            dispatch(setLoadingJobs(false));
        }
    };
}

export function favoriteJob(args) {
    const {
        countLabel,
        extraContent,
        job
    } = args;

    return async (dispatch) => {
        dispatch(setLoadingJobs(true));

        const jobToSave = {
            ...job,
            isFavorite: !job.isFavorite
        };

        try {
            await updateJobTemplate(job.jobTemplateId, jobToSave);
            dispatch(fetchJobTemplates({
                dispatcher: dispatch,
                extraContent,
                operatorCountLabel: countLabel
            }));
        } catch (err) {
            throw err;
        } finally {
            dispatch(setLoadingJobs(false));
        }
    };
}

export function setCutHistory(props) {
    const {history} = props;

    return {
        type: SET_CUT_HISTORY,
        history
    };
}

export function fetchCutHistory(props) {
    const {
        startTime,
        endTime
    } = props;

    return async (dispatch) => {
        const response = await getCutHistory(startTime, endTime);
        const history = response.cutHistory;

        dispatch(setCutHistory({
            history
        }));
    };
}

export function setSelectedMowingPatterns(props) {
    const {
        filteredPatterns,
        index,
        name,
        selectedMP,
        value
    } = props;

    if (!filteredPatterns) {
        const newSelectedMP = [...selectedMP];

        newSelectedMP[index][name] = value;
        return {
            type: SET_SELECTED_MOWING_PATTERNS,
            mowingPatterns: newSelectedMP
        };
    }

    const prevByKey = new Map(selectedMP.map((p) => [p.equipmentAreaId, p]));

    const newMowingPatterns = filteredPatterns.filter((p) => !prevByKey.has(p.equipmentAreaId));
    const currentMowingPatterns = filteredPatterns
        .map((p) => prevByKey.get(p.equipmentAreaId))
        .filter(Boolean);

    const newValues = [...currentMowingPatterns, ...newMowingPatterns];

    let selectedValues = newValues;

    if (newValues.length === selectedMP.length && newValues.every((p, i) => p === selectedMP[i])) {
        selectedValues = selectedMP;
    }
    return {
        type: SET_SELECTED_MOWING_PATTERNS,
        mowingPatterns: selectedValues
    };
}

export function resetState() {
    return {
        type: RESET_SETUP_STATE
    };
}

export function setFleetAndEquipment(props) {
    const {
        equipment,
        id
    } = props;

    return {
        type: SET_FLEET_EQUIPMENT,
        equipment,
        id
    };
}

export function setLoadingFleetAndEquipment(loading) {
    return {
        type: SET_LOADING_FLEET_EQUIPMENT,
        loading
    };
}

export function fetchFleetAndEquipment(useEquipmentManager = false) {
    return async (dispatch) => {
        dispatch(setLoadingFleetAndEquipment(true));

        try {
            const fleetAndEquipment = await getFleetEquipment(useEquipmentManager);

            dispatch(setFleetAndEquipment({
                equipment: fleetAndEquipment.equipment,
                id: fleetAndEquipment.fleetId
            }));
        } finally {
            dispatch(setLoadingFleetAndEquipment(false));
        }
    };
}

export function setRowOrder(value) {
    return {
        type: SET_ROW_ORDER,
        value
    };
}

export function setLoadingSetup(value) {
    return {
        type: SET_LOADING_SETUP,
        value
    };
}

export function setIsCopy(value) {
    return {
        type: SET_IS_COPY,
        value
    };
}

export function setCopyWorkboardMode() {
    return {
        type: SET_COPY_WORKBOARD_MODE
    };
}

export function setIsSaving(value) {
    return {
        type: SET_IS_SAVING,
        value
    };
}

export function setNameValidation(value) {
    return {
        type: SET_NAME_VALIDATION,
        value
    };
}
